import { Schedule } from "../models/Schedule";
import { Category } from "../models/Category";
import { Project } from "../models/Project";
import { WorkCategory, WORK_CATEGORY_LABELS, Status } from "../types";
import { getCategoryLevelColor } from "../config/categoryColors";

/**
 * Format a date as YYYY-MM-DD for CSV
 */
const formatDate = (date: Date | undefined): string => {
  if (!date) {
    return "";
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Escape CSV field (handle commas, quotes, newlines)
 */
const escapeCsvField = (field: string): string => {
  // Replace newlines with escaped newlines for CSV
  const normalizedField = field.replace(/\n/g, "\\n").replace(/\r/g, "\\r");

  if (
    normalizedField.includes(",") ||
    normalizedField.includes('"') ||
    normalizedField.includes("\\")
  ) {
    return `"${normalizedField.replace(/"/g, '""')}"`;
  }
  return normalizedField;
};

/**
 * Export schedule data to CSV format
 * @param schedule - Schedule instance to export
 * @param separator - CSV separator (default: ",", can use ";" for Excel compatibility)
 * @returns CSV string
 */
export const exportScheduleToCSV = (
  schedule: Schedule,
  separator: string = ","
): string => {
  const lines: string[] = [];

  // CSV Header - simplified without IDs
  const header = [
    "Type",
    "Level",
    "Name",
    "Color",
    "Status",
    "Start Date",
    "End Date",
    "Total Effort (jh)",
    `${WORK_CATEGORY_LABELS[WorkCategory.DEVELOPMENT]} (jh)`,
    `${WORK_CATEGORY_LABELS[WorkCategory.IA]} (jh)`,
    `${WORK_CATEGORY_LABELS[WorkCategory.PROJECT_MANAGEMENT]} (jh)`,
    `${WORK_CATEGORY_LABELS[WorkCategory.CONTENT_CREATION]} (jh)`,
    `${WORK_CATEGORY_LABELS[WorkCategory.COG_SCI]} (jh)`,
  ];
  lines.push(header.join(separator));

  // Get all categories in flattened order
  const categories = schedule.getFlattenedCategories();

  categories.forEach((category: Category) => {
    // Get only direct projects (not from subcategories)
    const directProjects = category.getProjects();
    const allProjects = category.getAllProjects();
    const definedProjects = allProjects.filter((p) => p.startDate && p.endDate);

    // Calculate category aggregates from all projects (including subcategories)
    let categoryStart: Date | undefined;
    let categoryEnd: Date | undefined;
    let categoryEffort = 0;

    if (definedProjects.length > 0) {
      const categoryStartDates = definedProjects.map((p) =>
        p.startDate!.getTime()
      );
      const categoryEndDates = definedProjects.map((p) => p.endDate!.getTime());
      categoryStart = new Date(Math.min(...categoryStartDates));
      categoryEnd = new Date(Math.max(...categoryEndDates));
      categoryEffort = allProjects.reduce((sum, p) => sum + p.effort, 0);
    }

    // Calculate category effort breakdown from all projects
    const categoryBreakdown = {
      [WorkCategory.DEVELOPMENT]: allProjects.reduce(
        (sum, p) => sum + (p.effortBreakdown[WorkCategory.DEVELOPMENT] || 0),
        0
      ),
      [WorkCategory.IA]: allProjects.reduce(
        (sum, p) => sum + (p.effortBreakdown[WorkCategory.IA] || 0),
        0
      ),
      [WorkCategory.PROJECT_MANAGEMENT]: allProjects.reduce(
        (sum, p) =>
          sum + (p.effortBreakdown[WorkCategory.PROJECT_MANAGEMENT] || 0),
        0
      ),
      [WorkCategory.CONTENT_CREATION]: allProjects.reduce(
        (sum, p) =>
          sum + (p.effortBreakdown[WorkCategory.CONTENT_CREATION] || 0),
        0
      ),
      [WorkCategory.COG_SCI]: allProjects.reduce(
        (sum, p) => sum + (p.effortBreakdown[WorkCategory.COG_SCI] || 0),
        0
      ),
    };

    // Add category row with level only
    const categoryRow = [
      "Category",
      category.level.toString(),
      escapeCsvField(category.name),
      category.color,
      "", // No status for categories
      formatDate(categoryStart),
      formatDate(categoryEnd),
      categoryEffort.toString(),
      categoryBreakdown[WorkCategory.DEVELOPMENT].toString(),
      categoryBreakdown[WorkCategory.IA].toString(),
      categoryBreakdown[WorkCategory.PROJECT_MANAGEMENT].toString(),
      categoryBreakdown[WorkCategory.CONTENT_CREATION].toString(),
      categoryBreakdown[WorkCategory.COG_SCI].toString(),
    ];
    lines.push(categoryRow.join(","));

    // Add project rows (only direct projects, not from subcategories)
    // Projects are indented one level more than their parent category
    directProjects.forEach((project: Project) => {
      const projectRow = [
        "Project",
        (category.level + 1).toString(), // Project level = category level + 1
        escapeCsvField(project.name),
        project.color,
        project.status || "",
        formatDate(project.startDate),
        formatDate(project.endDate),
        project.effort.toString(),
        (project.effortBreakdown[WorkCategory.DEVELOPMENT] || 0).toString(),
        (project.effortBreakdown[WorkCategory.IA] || 0).toString(),
        (
          project.effortBreakdown[WorkCategory.PROJECT_MANAGEMENT] || 0
        ).toString(),
        (
          project.effortBreakdown[WorkCategory.CONTENT_CREATION] || 0
        ).toString(),
        (project.effortBreakdown[WorkCategory.COG_SCI] || 0).toString(),
      ];
      lines.push(projectRow.join(","));
    });
  });

  return lines.join("\n");
};

/**
 * Download CSV file
 * @param csvContent - CSV string content
 * @param filename - Name of the file to download
 */
export const downloadCSV = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Export schedule to CSV and trigger download
 * @param schedule - Schedule to export
 * @param filename - Optional custom filename
 */
export const exportSchedule = (schedule: Schedule, filename?: string): void => {
  const csvContent = exportScheduleToCSV(schedule);
  const defaultFilename = `schedule-${
    new Date().toISOString().split("T")[0]
  }.csv`;
  downloadCSV(csvContent, filename || defaultFilename);
};

/**
 * Parse a date string in multiple formats
 * Supports: YYYY-MM-DD, DD/MM/YYYY
 */
const parseDate = (dateStr: string): Date => {
  if (!dateStr || !dateStr.trim()) {
    throw new Error("Empty date string");
  }

  // Check if it's DD/MM/YYYY format (contains /)
  if (dateStr.includes("/")) {
    const parts = dateStr.split("/");
    if (parts.length !== 3) {
      throw new Error(`Invalid date format: ${dateStr}`);
    }
    const [day, month, year] = parts.map(Number);
    console.log(`Parsing date DD/MM/YYYY: ${day}/${month}/${year}`);
    return new Date(year, month - 1, day);
  }

  // Otherwise assume YYYY-MM-DD format (contains -)
  if (dateStr.includes("-")) {
    const parts = dateStr.split("-");
    if (parts.length !== 3) {
      throw new Error(`Invalid date format: ${dateStr}`);
    }
    const [year, month, day] = parts.map(Number);
    console.log(`Parsing date YYYY-MM-DD: ${year}-${month}-${day}`);
    return new Date(year, month - 1, day);
  }

  throw new Error(
    `Unrecognized date format: ${dateStr}. Expected YYYY-MM-DD or DD/MM/YYYY`
  );
};

/**
 * Unescape CSV field (restore newlines and other special characters)
 */
const unescapeCsvField = (field: string): string => {
  return field.replace(/\\n/g, "\n").replace(/\\r/g, "\r");
};

/**
 * Temporary category structure for building hierarchy
 */
interface TempCategory {
  id: number;
  name: string;
  color: string;
  level: number;
  parentId: number | null;
  projects: Project[];
  children: TempCategory[];
}

/**
 * Convert temporary category tree to immutable Category instances
 */
function convertToCategory(temp: TempCategory): Category {
  const subcategories = temp.children.map(convertToCategory);

  return new Category({
    id: temp.id,
    name: temp.name,
    color: temp.color,
    level: temp.level,
    parentId: temp.parentId,
    projects: temp.projects,
    subcategories,
  });
}

/**
 * Parse CSV content and create Category array with hierarchical structure
 * Uses level and line order to reconstruct hierarchy without needing IDs
 * @param csvContent - CSV string content
 * @returns Array of top-level Category instances
 */
export const importScheduleFromCSV = (csvContent: string): Category[] => {
  const lines = csvContent.trim().split("\n");

  console.log("=== CSV IMPORT START ===");
  console.log("Total lines:", lines.length);

  if (lines.length < 2) {
    throw new Error("CSV file is empty or invalid");
  }

  // Detect separator: check if first line contains semicolon
  const separator = lines[0].includes(";") ? ";" : ",";
  console.log("Detected separator:", separator === ";" ? "semicolon" : "comma");
  console.log("Header line:", lines[0]);

  const dataLines = lines.slice(1);
  console.log("Data lines to process:", dataLines.length);

  // Stack to track current category at each level (mutable temp objects)
  const stack: TempCategory[] = [];
  const roots: TempCategory[] = [];

  let categoryIdCounter = 1;
  let projectIdCounter = 1;
  let lineNumber = 2; // Starting from line 2 (after header)

  let level = 0;
  let minCategoryLevel = Infinity; // Track minimum category level to normalize

  dataLines.forEach((line) => {
    if (!line.trim()) {
      console.log(`Line ${lineNumber}: Empty line, skipping`);
      lineNumber++;
      return;
    }

    // Parse CSV line (handle quoted fields with dynamic separator)
    const fields: string[] = [];
    let currentField = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          currentField += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === separator && !inQuotes) {
        fields.push(currentField.trim());
        currentField = "";
      } else {
        currentField += char;
      }
    }
    fields.push(currentField.trim());

    console.log(`\nLine ${lineNumber}: Parsed ${fields.length} fields`);
    console.log("Fields:", fields); // Show first 5 fields

    const [
      type,
      levelStr,
      name,
      color,
      status,
      startDateStr,
      endDateStr, // totalEffort (calculated)
      ,
      dev,
      ia,
      pm,
      content,
      cogSci,
    ] = fields;

    // Skip lines with empty type
    if (!type || !type.trim()) {
      console.log(`Line ${lineNumber}: Empty type field, skipping`);
      lineNumber++;
      return;
    }

    if (type === "Category") {
      level = parseInt(levelStr) || 0;
      minCategoryLevel = Math.min(minCategoryLevel, level);

      console.log(
        `Line ${lineNumber}: Type="${type}", Level=${level}, Name="${name}"`
      );
      const parentId =
        level > minCategoryLevel && stack[level - 1]
          ? stack[level - 1].id
          : null;

      const tempCategory: TempCategory = {
        id: categoryIdCounter++,
        name: unescapeCsvField(name),
        color: color || getCategoryLevelColor(level),
        level,
        parentId,
        projects: [],
        children: [],
      };

      console.log(
        `  → Creating Category: ID=${tempCategory.id}, ParentID=${parentId}, Level=${level}`
      );
      console.log(`     Stack depth before: ${stack.length}`);
      console.log(
        `     Min category level: ${minCategoryLevel}, Current level: ${level}`
      );

      // Add to parent or roots
      // If all categories start at level > 0 (e.g., level 1), treat minCategoryLevel as root
      const isRoot =
        minCategoryLevel === 0 ? level === 0 : level === minCategoryLevel;

      if (isRoot) {
        roots.push(tempCategory);
        console.log(`     Added to roots (total roots: ${roots.length})`);
      } else if (stack[level - 1]) {
        stack[level - 1].children.push(tempCategory);
        console.log(`     Added as child to: "${stack[level - 1].name}"`);
      } else {
        console.warn(`     WARNING: No parent found at level ${level - 1}!`);
      }

      // Update stack
      stack[level] = tempCategory;
      stack.length = level + 1; // Clear deeper levels
      console.log(`     Stack depth after: ${stack.length}`);
    } else if (type === "Project") {
      // Projects without level field belong to the most recent category
      // Projects with level field belong to category at (level - 1)
      let parentCategory: TempCategory | undefined;

      if (!levelStr || levelStr.trim() === "") {
        // Empty level: use the most recent category (last non-null in stack)
        parentCategory = stack.filter((c) => c).pop();
        console.log(
          `  → Creating Project, declared level=empty, using most recent category: ${
            parentCategory ? parentCategory.name : "undefined"
          }`
        );
      } else {
        // Explicit level: parse it and find parent
        const projectLevel = parseInt(levelStr);
        const parentLevel = projectLevel - 1;
        parentCategory = stack[parentLevel];
        console.log(
          `  → Creating Project, declared level=${projectLevel}, parent level=${parentLevel}, parent category = ${
            parentCategory ? parentCategory.name : "undefined"
          }`
        );
      }

      if (parentCategory) {
        const project = new Project({
          id: projectIdCounter++,
          name: unescapeCsvField(name),
          startDate: startDateStr ? parseDate(startDateStr) : undefined,
          endDate: endDateStr ? parseDate(endDateStr) : undefined,
          color: color || "#3b82f6",
          effortBreakdown: {
            [WorkCategory.DEVELOPMENT]: parseFloat(dev) || 0,
            [WorkCategory.IA]: parseFloat(ia) || 0,
            [WorkCategory.PROJECT_MANAGEMENT]: parseFloat(pm) || 0,
            [WorkCategory.CONTENT_CREATION]: parseFloat(content) || 0,
            [WorkCategory.COG_SCI]: parseFloat(cogSci) || 0,
          },
        });

        if (status) {
          console.log("Read status ", status);
          const normalizedStatus = status.trim();
          let mappedStatus: Status | undefined;

          // Fallbacks for common synonyms
          if (!mappedStatus) {
            switch (normalizedStatus.toLowerCase()) {
              case "in progress":
              case "in-progress":
              case "active":
              case "en cours":
                mappedStatus = Status.IN_PROGRESS;
                break;
              case "todo":
              case "to do":
              case "planned":
              case "a démarrer":
                mappedStatus = Status.PLANNED;
                break;
              case "done":
              case "completed":
              case "closed":
              case "terminé":
                mappedStatus = Status.DONE;
                break;
              default:
                mappedStatus = Status.UNDER_DISCUSSION;
            }
          }

          project.status = mappedStatus;
        }

        parentCategory.projects.push(project);
        console.log(
          `     Added project to category: "${parentCategory.name}" (${parentCategory.projects.length} projects total)`
        );
      } else {
        console.warn(`     WARNING: No parent category found for project!`);
        console.warn(
          `     Stack content:`,
          stack.filter((c) => c).map((c) => `L${c.level}:${c.name}`)
        );
      }
    } else {
      console.warn(`  → Unknown type: "${type}"`);
    }

    lineNumber++;
  });

  // Convert temporary structure to immutable Categories
  console.log("\n=== CONVERSION SUMMARY ===");
  console.log("Total root categories:", roots.length);
  console.log(
    "Root categories:",
    roots.map(
      (r) =>
        `"${r.name}" (${r.children.length} children, ${r.projects.length} projects)`
    )
  );

  const result = roots.map(convertToCategory);

  console.log("\n=== IMPORT COMPLETE ===");
  console.log("Categories created:", categoryIdCounter - 1);
  console.log("Projects created:", projectIdCounter - 1);
  console.log("Returning categories:", result.length);

  return result;
};

/**
 * Read CSV file from file input and parse it
 * @param file - File object from input
 * @returns Promise that resolves to array of Category instances
 */
export const readCSVFile = (file: File): Promise<Category[]> => {
  return new Promise((resolve, reject) => {
    console.log("=== READING CSV FILE ===");
    console.log("File name:", file.name);
    console.log("File size:", file.size, "bytes");
    console.log("File type:", file.type);

    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const csvContent = event.target?.result as string;
        console.log(
          "File read successfully, content length:",
          csvContent.length
        );
        console.log("First 200 characters:", csvContent.substring(0, 200));

        const categories = importScheduleFromCSV(csvContent);

        console.log("Import successful!");
        resolve(categories);
      } catch (error) {
        console.error("=== IMPORT ERROR ===");
        console.error("Error message:", error);
        console.error("Error stack:", (error as Error).stack);
        reject(error);
      }
    };

    reader.onerror = () => {
      console.error("=== FILE READ ERROR ===");
      console.error("Failed to read file");
      reject(new Error("Failed to read file"));
    };

    reader.readAsText(file);
  });
};
